import * as React from 'react';
import cx from 'classnames';
import { Icon, IGlyph } from '@treelab/panda';

import { TypographyStyle, ThemeStyle } from '../../types';
import { Typography } from '../../../components/Typography';
import { ADD_GLYPH } from '../../base/icons';

/**
 * Button component without background styling.
 */
export interface IButtonPlainProps {
  /** Optional passed classnames */
  className?: string;
  /** Indicates use of default Icon */
  hasDefaultIcon?: boolean;
  /** Glyph for svg */
  icon?: IGlyph;
  /** Sizing for Glyph */
  iconSize?: { height?: number; width?: number };
  /** Default is true */
  isTabable?: boolean;
  /** Option for disabled */
  isDisabled?: boolean;
  /** Is small */
  isSmall?: boolean;
  /** On click action for the button. */
  onClick: () => void;
  /** Test ids */
  testId?: string;
  /** Theme style for button colors */
  theme?: ThemeStyle;
  /** Glyph for svg */
  trailingIcon?: IGlyph;
  /** Custom size if needed */
  trailingIconSize?: { height?: number; width?: number };
}

const PLAIN_THEME = {
  [ThemeStyle.black]: 'panda-btn-plain--theme-black',
  [ThemeStyle.gray]: 'panda-btn-plain--theme-gray',
  [ThemeStyle.blue]: 'panda-btn-plain--theme-blue',
  [ThemeStyle.red]: 'panda-btn-plain--theme-red',
  [ThemeStyle.white]: 'panda-btn-plain--theme-white',
};

export const ButtonPlain: React.FC<IButtonPlainProps> = ({
  className,
  children,
  hasDefaultIcon,
  icon,
  iconSize,
  isTabable = true,
  isDisabled = false,
  isSmall = false,
  onClick,
  testId,
  theme = ThemeStyle.blue,
  trailingIcon,
  trailingIconSize,
}) => {
  const combinedIconClass = cx({
    'space-right-small': !!children,
  });
  const combinedClass = cx({
    'panda-btn-plain': true,
    'panda-btn-plain--disabled': isDisabled,
    'hover-cursor': !isDisabled,
    [className || '']: !!className,
    [PLAIN_THEME[theme]]: true,
  });
  const defaultIconSize = isSmall ? { width: 14, height: 14 } : { width: 16, height: 16 };

  return (
    <button
      className={combinedClass}
      disabled={isDisabled}
      onClick={onClick}
      data-test={testId}
      tabIndex={isTabable ? 0 : -1}
      type="button"
    >
      {icon || hasDefaultIcon ? (
        <>
          <Icon
            className={combinedIconClass}
            icon={hasDefaultIcon ? ADD_GLYPH : icon!}
            size={iconSize || defaultIconSize}
          />
          {children && (
            <Typography type={isSmall ? TypographyStyle.P2 : TypographyStyle.P1}>
              {children}
            </Typography>
          )}
        </>
      ) : (
        <Typography type={isSmall ? TypographyStyle.P2 : TypographyStyle.P1}>{children}</Typography>
      )}
      {trailingIcon && (
        <Icon
          icon={trailingIcon}
          size={trailingIconSize || defaultIconSize}
          className="space-left-small"
        />
      )}
    </button>
  );
};
